if (!customElements.get('wlsn-header')) {
    class WLSNHeader extends HTMLElement {
        constructor() {
            super();

            this.classes = {
                active: 'active',
                sticky: 'sticky',
                open: 'open',
                hasMegamenu: 'has-megamenu',
                shopifySection: 'shopify-section',
                mobileMenuActive: 'mobile-menu-active',
                menuToggle: 'js-wlsn-header-menu-toggle',
                promoBar: 'js-wlsn-promo-bar',
                megamenu: 'js-wlsn-megamenu',
                megamenuMobile: 'js-wlsn-megamenu-mobile',
                menuLinkWrapper: 'js-wlsn-header-menu-link-wrapper',
                cartDrawerTrigger: 'js-wlsn-header-cart-drawer-trigger',
                cartDrawer: 'js-wlsn-cart-drawer'
            };

            this.elements = {
                menuToggle: this.querySelector(`.${this.classes.menuToggle}`),
                headerParent: this.closest(`.${this.classes.shopifySection}`),
                cartDrawerTrigger: this.querySelector(`.${this.classes.cartDrawerTrigger}`),
                cartDrawer: document.querySelector(`.${this.classes.cartDrawer}`)
            };

            this.handleNavLinkClick = this.handleNavLinkClick.bind(this);
            this.handleDocumentClick = this.handleDocumentClick.bind(this);
            this.handleMenuToggleClick = this.handleMenuToggleClick.bind(this);
            // Close any open megamenu and the mobile drawer when the header search panel opens.
            this.handleSearchOpen = this.handleSearchOpen.bind(this);
        }

        connectedCallback() {
            // Move megamenus into their corresponding menu link wrappers
            const megamenus = document.querySelectorAll(`.${this.classes.megamenu}`);
            megamenus.forEach(megamenu => {
                const id = megamenu.getAttribute('data-megamenu-id');
                if (!id) return;
                const navLinkWrapper = this.querySelector(`.${this.classes.menuLinkWrapper}[data-header-nav-link-id='${id}']`);
                if (navLinkWrapper && !navLinkWrapper.classList.contains(this.classes.hasMegamenu)) {
                    navLinkWrapper.appendChild(megamenu);
                    navLinkWrapper.classList.add(this.classes.hasMegamenu);
                }
            });

            // Cache menu link wrappers
            this.elements.menuLinkWrappers = this.querySelectorAll(`.${this.classes.menuLinkWrapper}`);
            // Cache megamenus (now children of wrappers with hasMegamenu)
            this.elements.megamenus = this.querySelectorAll(`.${this.classes.megamenu}`);

            // Add click events to menu links
            this.elements.menuLinks = this.querySelectorAll('.wlsn-header__menu-link');
            if (this.elements.menuLinks.length) {
                this.addNavLinkEvents('add');
                document.addEventListener('click', this.handleDocumentClick);
            }

            document.addEventListener('wlsn:search:open', this.handleSearchOpen);

            // Restore megamenu height logic
            if (this.elements.megamenus.length) {
                this.setMegamenuHeight();
                window.addEventListener('resize', this.setMegamenuHeight.bind(this));
                if (this.elements.headerParent?.classList.contains(this.classes.sticky)) {
                    window.addEventListener('scroll', this.setMegamenuHeight.bind(this));
                }
            }

            // Mobile menu toggle logic
            if (this.elements.menuToggle) {
                this.elements.menuToggle.addEventListener('click', this.handleMenuToggleClick);
            }
            this.handleCartDrawerTriggerClick();
        }

        disconnectedCallback() {
            if (this.elements.menuLinks && this.elements.menuLinks.length) {
                this.addNavLinkEvents('remove');
                document.removeEventListener('click', this.handleDocumentClick);
            }
            if (this.elements.menuToggle) {
                this.elements.menuToggle.removeEventListener('click', this.handleMenuToggleClick);
            }
            document.removeEventListener('wlsn:search:open', this.handleSearchOpen);
        }

        addNavLinkEvents(action) {
            const method = action === 'add' ? 'addEventListener' : 'removeEventListener';
            this.elements.menuLinks.forEach(link => {
                link[method]('click', this.handleNavLinkClick);
            });
        }

        handleNavLinkClick(event) {
            const link = event.currentTarget;
            const wrapper = link.closest(`.${this.classes.menuLinkWrapper}`);
            if (wrapper && wrapper.classList.contains(this.classes.hasMegamenu)) {
                event.preventDefault();
                event.stopPropagation();
                const isActive = wrapper.classList.contains(this.classes.active);
                this.closeAllMegamenus();
                if (!isActive) {
                    wrapper.classList.add(this.classes.active);
                    // Let the header search panel close itself when a megamenu opens.
                    document.dispatchEvent(new CustomEvent('wlsn:nav:open'));
                }
            }
        }

        handleDocumentClick(event) {
            // Only handle document clicks for desktop
            if (window.innerWidth <= 1024) return;
            const isMenuLink = event.target.closest && event.target.closest(`.${this.classes.menuLinkWrapper}`);
            if (this.contains(event.target) && isMenuLink) {
                return;
            }
            if ([...this.elements.menuLinkWrappers].some(w => w.classList.contains(this.classes.active))) {
                this.closeAllMegamenus();
            }
        }

        handleMenuToggleClick() {
            if (window.innerWidth > 1024) return;
            const isActive = this.classList.contains(this.classes.mobileMenuActive);
            this.classList.toggle(this.classes.mobileMenuActive, !isActive);
        }

        /* Opening search dismisses the drawer rather than making the user close it first. */
        handleSearchOpen() {
            this.closeAllMegamenus();
            this.classList.remove(this.classes.mobileMenuActive);
        }

        closeAllMegamenus() {
            if (this.elements.menuLinkWrappers) {
                this.elements.menuLinkWrappers.forEach(w => w.classList.remove(this.classes.active));
            }
        }

        handleCartDrawerTriggerClick() {
            if(this.elements.cartDrawer && this.elements.cartDrawerTrigger) {
                this.elements.cartDrawerTrigger.addEventListener('click', () => {
                    this.elements.cartDrawer.toggleAttribute('open');
                });
            }
        }

        /*
         * Two promo bars exist per page (header nav group + in-template) and which one shows
         * depends on the width, so this can't be cached. querySelector would always return the
         * group's copy, hidden below 768px, sizing the drawer against a height of 0.
         */
        getVisiblePromoBar() {
            const promoBars = document.querySelectorAll(`.${this.classes.promoBar}`);
            return [...promoBars].find(bar => bar.getClientRects().length > 0) || null;
        }

        setMegamenuHeight() {
            if (window.innerWidth > 1024) return;

            const headerHeight = this.getBoundingClientRect().height;
            const viewportHeight = window.innerHeight;
            const scrollAmount = window.scrollY;
            
            // Get initial promo bar height
            let promoBarHeight = 0;
            const promoBar = this.getVisiblePromoBar();
            if (promoBar) {
                const initialPromoBarHeight = promoBar.getBoundingClientRect().height;
                // Adjust promo bar height based on scroll position
                if (scrollAmount > 0) {
                    promoBarHeight = Math.max(0, initialPromoBarHeight - scrollAmount);
                } else {
                    promoBarHeight = initialPromoBarHeight;
                }
            }
            const megamenuHeight = `${viewportHeight - headerHeight - promoBarHeight}px`;
            if (this.elements.megamenus) {
                this.elements.megamenus.forEach(megamenu => {
                    megamenu.style.height = megamenuHeight;
                });
            }
        }
    }
    customElements.define('wlsn-header', WLSNHeader);
}
